import React, { useEffect, useState, useCallback } from "react";
import { Download, Smartphone, X, Share, Plus } from "lucide-react";

/**
 * Cross-platform "Install App" button for our PWA.
 * - Chrome/Edge/Android: uses the native beforeinstallprompt event
 * - iOS Safari: shows a friendly modal with the manual Add-to-Home-Screen steps
 * - Already-installed (display-mode: standalone): hides itself
 */
function isIOS() {
  if (typeof navigator === "undefined") return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

export default function InstallAppButton({ variant = "navbar" }) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(isStandalone());
  const [iosModalOpen, setIosModalOpen] = useState(false);
  const ios = isIOS();

  useEffect(() => {
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const handleClick = useCallback(async () => {
    if (ios) {
      setIosModalOpen(true);
      return;
    }
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") setInstalled(true);
      setDeferredPrompt(null);
      return;
    }
    // Fallback: desktop browsers without prompt event still show modal w/ instructions
    setIosModalOpen(true);
  }, [deferredPrompt, ios]);

  if (installed) return null;

  // Show only when actually installable, OR on iOS (which always supports A2HS),
  // OR as the prominent variant which always shows.
  const showable = ios || deferredPrompt || variant === "prominent";
  if (!showable) return null;

  const Btn =
    variant === "prominent" ? (
      <button
        type="button"
        onClick={handleClick}
        className="inline-flex items-center gap-2 bg-zinc-900 text-white border border-zinc-900 hover:bg-red-600 hover:border-red-600 px-5 py-3 text-sm font-bold uppercase tracking-widest transition-colors"
        data-testid="install-app-btn-prominent"
      >
        <Download size={16} /> Install Paws on Record App
      </button>
    ) : (
      <button
        type="button"
        onClick={handleClick}
        className="hidden md:inline-flex items-center gap-1.5 border border-zinc-900 hover:bg-zinc-900 hover:text-white px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest transition-colors"
        title="Install Paws on Record as an app on your device"
        data-testid="install-app-btn"
      >
        <Smartphone size={13} /> Install App
      </button>
    );

  return (
    <>
      {Btn}
      {iosModalOpen && (
        <InstallInstructionsModal
          ios={ios}
          onClose={() => setIosModalOpen(false)}
        />
      )}
    </>
  );
}

function InstallInstructionsModal({ ios, onClose }) {
  return (
    <div
      className="fixed inset-0 z-[10000] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
      onClick={onClose}
      data-testid="install-instructions-modal"
    >
      <div
        className="bg-white border-2 border-zinc-900 max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-zinc-900 text-white px-5 py-4 flex items-center justify-between border-b border-red-600">
          <div>
            <div className="label-eyebrow text-red-500">Install · App</div>
            <div className="font-display font-black text-xl mt-1">
              Add Paws on Record to your home screen
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-white hover:text-red-500"
            aria-label="Close"
            data-testid="install-modal-close"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-5 text-sm text-zinc-800 leading-relaxed">
          {ios ? (
            <>
              <p className="mb-4">
                On <strong>iPhone or iPad</strong>, install Paws on Record as an
                app in two taps:
              </p>
              <ol className="space-y-3">
                <li className="flex gap-3">
                  <span className="w-6 h-6 bg-zinc-900 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                    1
                  </span>
                  <span>
                    Tap the <Share size={14} className="inline mx-1 -mt-0.5" />
                    <strong>Share</strong> button at the bottom of Safari.
                  </span>
                </li>
                <li className="flex gap-3">
                  <span className="w-6 h-6 bg-zinc-900 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                    2
                  </span>
                  <span>
                    Scroll down and tap{" "}
                    <Plus size={14} className="inline mx-1 -mt-0.5" />
                    <strong>"Add to Home Screen"</strong>, then{" "}
                    <strong>"Add"</strong>.
                  </span>
                </li>
              </ol>
              <p className="mt-4 text-xs text-zinc-500">
                The app icon will appear on your home screen alongside other
                apps. No App Store needed.
              </p>
            </>
          ) : (
            <>
              <p className="mb-4">
                Install Paws on Record as a desktop app:
              </p>
              <ol className="space-y-3">
                <li className="flex gap-3">
                  <span className="w-6 h-6 bg-zinc-900 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                    1
                  </span>
                  <span>
                    Look for the <Download size={14} className="inline mx-1 -mt-0.5" />
                    <strong>install icon</strong> on the right side of your
                    browser address bar (Chrome, Edge, Brave) and click it.
                  </span>
                </li>
                <li className="flex gap-3">
                  <span className="w-6 h-6 bg-zinc-900 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                    2
                  </span>
                  <span>
                    Or open the browser menu (⋮) and choose{" "}
                    <strong>"Install Paws on Record"</strong> /{" "}
                    <strong>"Install app"</strong>.
                  </span>
                </li>
              </ol>
              <p className="mt-4 text-xs text-zinc-500">
                On Safari (Mac), click the Share button → Add to Dock. On
                Firefox, install isn't supported — bookmark the site instead.
              </p>
            </>
          )}
        </div>

        <div className="border-t border-zinc-200 px-5 py-3 bg-zinc-50">
          <button
            type="button"
            onClick={onClose}
            className="text-xs font-bold uppercase tracking-widest hover:text-red-600"
            data-testid="install-modal-got-it"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}
